import React, { useState, useEffect, useMemo } from "react";
import {
  Card,
  CardContent,
  Typography,
  Button,
  Box,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Checkbox,
  Alert,
  IconButton,
  Menu,
  MenuItem,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  FormHelperText,
  InputAdornment,
  Snackbar,
  Autocomplete,
  styled,
  Chip,
} from "@mui/material";

import {
  Add as AddIcon,
  Search as SearchIcon,
  FileDownload as ExportIcon,
  Description as LedgerIcon,
  Assessment as ReportIcon,
  ExpandMore,
  KeyboardArrowUp,
  KeyboardArrowDown,
  LabelOutlined,
  FilterAlt,
} from "@mui/icons-material";

import MDButton from "../../components/MDButton";
import Icon from "@mui/material/Icon";
import MDDatePicker from "../../components/MDDatePicker";
import useMediaQuery from "@mui/material/useMediaQuery";
import { states, validateZipOrPostalCode } from "../../utils/States";
import { SelectChangeEvent } from "@mui/material";
import DataTable from "../../lib/Tables/DataTable";
import InnerTable from "../../lib/Tables/InnerTable";
import ExcelExportUtil from "../../utils/DataExportUtils";
import { get, post } from "requests";
import withDommer from "../withDommer";
import OfferCustomSelect from "pages/offers/OfferCustomSelect";

interface DateFilters {
  fromDate: Date | null;
  toDate: Date | null;
  byPeriod: string | null;
}

interface ValidationRule {
  required: boolean;
  message: string;
  pattern?: RegExp;
  validate?: (value: string) => boolean;
}

interface ValidationRules {
  [key: string]: ValidationRule;
}

interface ValidationErrors {
  name?: string;
  email?: string;
  phoneNumber?: string;
  contactPerson?: string;
  address?: string;
  state?: string;
  city?: string;
  zip?: string;
  abaNumber?: string;
  accountNumber?: string;
  bankName?: string;
}

export interface SyndicatorSummary {
  partyId: number;
  syndicatorName: string;
  labels: string[];
  cashAvailable: number;
  dealBalanceOwed: number;
  paybackRtr: number;
  cashCollected: number;
  lastDealFunded: string | null;
  dealsFunded: number;
  commissionsPaid: number;
  feesPaid: number;
  portfolioValue: number;
  deposits: number;
  withdrawals: number;
  totalDealsFunded: number;
  totalOpenDeals: number;
  totalPaidOff: number;
  totalDefaulted: number;
  avgDealSize: number;
  avgFactorRate: number;
  cashOnCashMoic: number;
  totalInvested: number;
}

export interface ApiResponse<T> {
  data: T;
  message?: string;
  success: boolean;
}

const Syndicators: React.FC = () => {
  const ACCESS_TOKEN = "authorizationToken";
  const [selectedSyndicator, setSelectedSyndicator] = useState<any>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [displayLedgerReportAlert, setDisplayLedgerReportAlert] = useState(false);
  const [loading, setLoading] = useState(false);
  const [syndicators, setSyndicators] = useState<any[]>([]);
  const [investors, setInvestors] = useState<any[]>([]);
  const [selectedSyndicatorIds, setSelectedSyndicatorIds] = useState<number[]>([]);
  const [selectedSyndicatorNames, setSelectedSyndicatorNames] = useState<string[]>([]);
  const [syndicatorsMenuAnchor, setSyndicatorsMenuAnchor] = useState<null | HTMLElement>(null);
  const [dropdownOpen, setdropdownOpen] = useState(false);
  const [rangeOpen, setRangeOpen] = useState(false);
  const [modalError, setModalError] = useState("");

  const [users, setUsers] = useState<any[]>([]);
  const [summaryData, setSummaryData] = useState<any[]>([]);
  const [countCount, setTotalCount] = useState<any[]>([]);
  const [selectedSyndicatorRow, setSelectedSyndicatorRow] = useState<any>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [errors, setErrors] = useState<ValidationErrors>({});
  const [ledgerLoading, setLedgerLoading] = useState(false);
  const [showLedgerModal, setShowLedgerModal] = useState(false);
  const [reportsMenuAnchor, setReportsMenuAnchor] = useState<null | HTMLElement>(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success" as "success" | "error" | "warning" | "info",
  });
  const [dateFilters, setDateFilters] = useState<DateFilters>({
    fromDate: null,
    toDate: null,
    byPeriod: null,
  });
  const [filters, setFilters] = useState({
    fromDate: null,
    toDate: null,
    byPeriod: null,
    label: [],
    syndicatorIds: [],
    syndicatorNames: [],
    searchTerm: "",
  });

  const isToday = (date: Date): boolean => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const compareDate = new Date(date);
    compareDate.setHours(0, 0, 0, 0);
    return compareDate.getTime() === today.getTime();
  };

  const showSnackbar = (
    message: string,
    severity: "success" | "error" | "warning" | "info" = "success"
  ) => {
    setSnackbar({
      open: true,
      message,
      severity,
    });
  };

  const [summaryBoxes, setSummaryBoxes] = useState([
    { title: "Cash Available", value: "$0", color: "#4caf50" },
    { title: "Total Deals Funded", value: "0", color: "#666666" },
    { title: "Total Open Deals", value: "0", color: "#666666" },
    { title: "Total Paid Off", value: "0", color: "#666666" },
    { title: "Total Defaulted", value: "0", color: "#f44336" },
    { title: "Avg Deal Size", value: "$0", color: "#666666" },
    { title: "Avg Factor Rate", value: "0", color: "#666666" },
    { title: "Cash-On-Cash (MOIC)", value: "0%", color: "#666666" },
    { title: "Total Portfolio Value", value: "$0", color: "#666666" },
  ]);

  const [labelsMenu, setLabelsMenu] = useState<any[]>([]);

  const updateSummaryBoxes = (summaryData: SyndicatorSummary[]) => {
    if (summaryData.length === 0) {
      setSummaryBoxes([
        { title: "Cash Available", value: "$0", color: "#4caf50" },
        { title: "Total Deals Funded", value: "0", color: "#666666" },
        { title: "Total Open Deals", value: "0", color: "#666666" },
        { title: "Total Paid Off", value: "0", color: "#666666" },
        { title: "Total Defaulted", value: "0", color: "#f44336" },
        { title: "Avg Deal Size", value: "$0", color: "#666666" },
        { title: "Avg Factor Rate", value: "0", color: "#666666" },
        { title: "Cash-On-Cash (MOIC)", value: "0%", color: "#666666" },
        { title: "Total Portfolio Value", value: "$0", color: "#666666" },
      ]);
      return;
    }

    const totals = summaryData.reduce(
      (acc, item) => ({
        cashAvailable: acc.cashAvailable + (item.cashAvailable || 0),
        totalDealsFunded: acc.totalDealsFunded + (item.totalDealsFunded || 0),
        totalOpenDeals: acc.totalOpenDeals + (item.totalOpenDeals || 0),
        totalPaidOff: acc.totalPaidOff + (item.totalPaidOff || 0),
        totalDefaulted: acc.totalDefaulted + (item.totalDefaulted || 0),
        totalPortfolioValue: acc.totalPortfolioValue + (item.portfolioValue || 0),
        sumAvgDealSize: acc.sumAvgDealSize + (item.avgDealSize || 0),
        sumAvgFactorRate: acc.sumAvgFactorRate + (item.avgFactorRate || 0),
        sumCashOnCashMoic: acc.sumCashOnCashMoic + (item.cashOnCashMoic || 0),
        countWithAvgDealSize: acc.countWithAvgDealSize + (item.avgDealSize > 0 ? 1 : 0),
        countWithAvgFactorRate: acc.countWithAvgFactorRate + (item.avgFactorRate > 0 ? 1 : 0),
        countWithMoic: acc.countWithMoic + (item.cashOnCashMoic > 0 ? 1 : 0),
      }),
      {
        cashAvailable: 0,
        totalDealsFunded: 0,
        totalOpenDeals: 0,
        totalPaidOff: 0,
        totalDefaulted: 0,
        totalPortfolioValue: 0,
        sumAvgDealSize: 0,
        sumAvgFactorRate: 0,
        sumCashOnCashMoic: 0,
        countWithAvgDealSize: 0,
        countWithAvgFactorRate: 0,
        countWithMoic: 0,
      }
    );

    const avgDealSize =
      totals.countWithAvgDealSize > 0 ? totals.sumAvgDealSize / totals.countWithAvgDealSize : 0;

    const avgFactorRate =
      totals.countWithAvgFactorRate > 0
        ? totals.sumAvgFactorRate / totals.countWithAvgFactorRate
        : 0;

    const cashOnCashMoic =
      totals.countWithMoic > 0 ? totals.sumCashOnCashMoic / totals.countWithMoic : 0;

    setSummaryBoxes([
      {
        title: "Cash Available",
        value: `$${totals.cashAvailable.toLocaleString("en-US", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}`,
        color: totals.cashAvailable >= 0 ? "#4caf50" : "#f44336",
      },
      {
        title: "Total Deals Funded",
        value: totals.totalDealsFunded.toString(),
        color: "#666666",
      },
      {
        title: "Total Open Deals",
        value: totals.totalOpenDeals.toString(),
        color: "#666666",
      },
      {
        title: "Total Paid Off",
        value: totals.totalPaidOff.toString(),
        color: "#666666",
      },
      {
        title: "Total Defaulted",
        value: totals.totalDefaulted.toString(),
        color: totals.totalDefaulted > 0 ? "#f44336" : "#666666",
      },
      {
        title: "Avg Deal Size",
        value: `$${avgDealSize.toLocaleString("en-US", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}`,
        color: "#666666",
      },
      {
        title: "Avg Factor Rate",
        value: avgFactorRate.toFixed(3),
        color: "#666666",
      },
      {
        title: "Cash-On-Cash (MOIC)",
        value: `${(cashOnCashMoic * 100).toFixed(2)}%`,
        color: cashOnCashMoic >= 1 ? "#4caf50" : "#f44336",
      },
      {
        title: "Total Portfolio Value",
        value: `$${totals.totalPortfolioValue.toLocaleString("en-US", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}`,
        color: "#666666",
      },
    ]);
  };

  const validationRules: ValidationRules = {
    name: { required: true, message: "Name is required" },
    email: {
      required: false,
      pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
      message: "Valid email is required",
    },
    phoneNumber: {
      required: false,
      pattern: /^\(\d{3}\) \d{3}-\d{4}$/,
      message: "Valid phone number is required (e.g., (123)456-7890)",
    },
    contactPerson: { required: true, message: "Contact person is required" },
    address: { required: false, message: "Address is required" },
    state: { required: false, message: "State is required" },
    city: { required: false, message: "City is required" },
    zip: {
      required: false,
      message: "Valid ZIP/Postal code is required",
    },
    abaNumber: { required: false, message: "ABA Number is required", pattern: /^\d+$/ },
    accountNumber: { required: false, message: "Account Number is required" },
    bankName: { required: false, message: "Bank Name is required" },
  };
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phoneNumber: "",
    contactPerson: "",
    contactPersonId: "",
    address: "",
    state: "",
    city: "",
    zip: "",
    abaNumber: "",
    accountNumber: "",
    bankName: "",
    isFunder: false,
    isInvestor: true,
    isOverCollection: false,
  });

  const getPastelColor = (name: any) => {
    try {
      const nameLower = name.toLowerCase().trim();

      const specialColors: any = {
        active: [120, 60, 75],
        inactive: [0, 60, 75],
        pending: [45, 70, 75],
        completed: [180, 60, 75],
        success: [120, 65, 75],
        error: [0, 70, 75],
        warning: [45, 80, 75],
        info: [210, 70, 75],
        draft: [270, 50, 75],
        archived: [30, 40, 70],
        enabled: [120, 60, 75],
        disabled: [0, 40, 70],
        open: [120, 65, 75],
        closed: [0, 60, 75],
        new: [180, 70, 75],
        old: [30, 40, 70],
        high: [0, 70, 75],
        low: [240, 60, 75],
        medium: [45, 70, 75],
        on: [120, 70, 75],
        off: [0, 60, 75],
        yes: [120, 70, 75],
        no: [0, 70, 75],
        true: [120, 70, 75],
        false: [0, 70, 75],
      };

      if (specialColors[nameLower]) {
        const [h, s, l] = specialColors[nameLower];
        return `hsl(${h}, ${s}%, ${l}%)`;
      }

      let hash = 0;
      for (let i = 0; i < name.length; i++) {
        hash = name.charCodeAt(i) + ((hash << 5) - hash);
        hash = hash & hash;
      }

      const hueSpacing = 72;
      const baseHue = (Math.abs(hash) * hueSpacing) % 360;

      const offset1 = (baseHue + 72) % 360;
      const offset2 = (baseHue + 144) % 360;
      const offset3 = (baseHue + 216) % 360;
      const offset4 = (baseHue + 288) % 360;

      const hueChoices = [baseHue, offset1, offset2, offset3, offset4];
      const selectedHue = hueChoices[Math.abs(hash >> 3) % 5];

      const saturationGroups = [45, 55, 65];
      const selectedSaturation = saturationGroups[Math.abs(hash >> 5) % 3];

      const lightnessGroups = [65, 75, 85];
      const selectedLightness = lightnessGroups[Math.abs(hash >> 7) % 3];

      return `hsl(${selectedHue}, ${selectedSaturation}%, ${selectedLightness}%)`;
    } catch (error) {
      console.error("Error generating pastel color:", error);
      return "hsl(0, 0%, 50%)";
    }
  };

  const resetFormData = () => {
    setFormData({
      name: "",
      email: "",
      phoneNumber: "",
      contactPerson: "",
      contactPersonId: "",
      address: "",
      state: "",
      city: "",
      zip: "",
      abaNumber: "",
      accountNumber: "",
      bankName: "",
      isFunder: false,
      isInvestor: true,
      isOverCollection: false,
    });
    setErrors({});
  };

  const TruncatedCell = ({
    value,
    align = "left",
    isCurrency = false,
  }: {
    value: any;
    align?: "left" | "right" | "center";
    isCurrency?: boolean;
  }) => {
    const formattedValue =
      isCurrency && value !== null && value !== undefined
        ? `$${value.toLocaleString("en-US", { minimumFractionDigits: 2 })}`
        : value;

    return (
      <div
        style={{
          fontFamily: "Roboto",
          fontWeight: 400,
          fontSize: "12px",
          lineHeight: "140%",
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
          maxWidth: "100%",
          textAlign: align,
          padding: align === "right" ? "0 16px 0 0" : "0 0 0 12px",
        }}
        title={String(formattedValue || "")}
      >
        {formattedValue == null || formattedValue === undefined ? "" : formattedValue}
      </div>
    );
  };

  const columns = useMemo(
    () => [
      {
        Header: "SYNDICATOR NAME",
        accessor: "name",
        width: 120,
        Cell: ({ value }: { value: string }) => <TruncatedCell value={value} align="left" />,
      },
      {
        Header: "LABELS",
        accessor: "labels",
        width: 180,
        Cell: ({ row }: any) => {
          const labels = row.original.labels || [];
          return (
            <div style={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
              {labels.map((label: string) => (
                <div
                  key={label}
                  style={{
                    backgroundColor: getPastelColor(label),
                    color: "#333",
                    padding: "1px 6px",
                    borderRadius: "6px",
                    fontSize: "12px",
                    whiteSpace: "nowrap",
                  }}
                >
                  {label}
                </div>
              ))}
            </div>
          );
        },
      },
      {
        Header: "CASH AVAILABLE",
        accessor: "cashAvailable",
        width: 100,
        Cell: ({ value }: { value: number }) => (
          <TruncatedCell value={value} align="right" isCurrency={true} />
        ),
      },
      {
        Header: "DEAL BALANCE OWED",
        accessor: "dealBalanceOwed",
        width: 120,
        Cell: ({ value }: { value: number }) => (
          <TruncatedCell value={value} align="right" isCurrency={true} />
        ),
      },
      {
        Header: "PAYBACK(RTR)",
        accessor: "payback",
        width: 90,
        Cell: ({ value }: { value: number }) => (
          <TruncatedCell value={value} align="right" isCurrency={true} />
        ),
      },
      {
        Header: "CASH COLLECTED",
        accessor: "cashCollected",
        width: 100,
        Cell: ({ value }: { value: number }) => (
          <TruncatedCell value={value} align="right" isCurrency={true} />
        ),
      },
      {
        Header: "LAST DEAL FUNDED",
        accessor: "lastDealFunded",
        width: 110,
        Cell: ({ value }: { value: string }) => <TruncatedCell value={value} align="left" />,
      },
      {
        Header: "DEALS FUNDED",
        accessor: "dealsFunded",
        width: 90,
        Cell: ({ value }: { value: number }) => <TruncatedCell value={value} align="center" />,
      },
      {
        Header: "COMMISSIONS PAID",
        accessor: "commissionsPaid",
        width: 120,
        Cell: ({ value }: { value: number }) => (
          <TruncatedCell value={value} align="right" isCurrency={true} />
        ),
      },
      {
        Header: "FEES PAID",
        accessor: "feesPaid",
        width: 70,
        Cell: ({ value }: { value: number }) => (
          <TruncatedCell value={value} align="right" isCurrency={true} />
        ),
      },
      {
        Header: "PORTFOLIO VALUE",
        accessor: "portfolioValue",
        width: 110,
        Cell: ({ value }: { value: number }) => (
          <TruncatedCell value={value} align="right" isCurrency={true} />
        ),
      },
      {
        Header: "DEPOSITS",
        accessor: "deposits",
        width: 80,
        Cell: ({ value }: { value: number }) => (
          <TruncatedCell value={value} align="right" isCurrency={true} />
        ),
      },
      {
        Header: "WITHDRAWALS",
        accessor: "withdrawals",
        width: 90,
        Cell: ({ value }: { value: number }) => (
          <TruncatedCell value={value} align="right" isCurrency={true} />
        ),
      },
    ],
    []
  );

  useEffect(() => {
    fetchSummaryData();
    fetchUsers();
    fetchSyndicators();
  }, []);

  const isSyndicatorLimitedLogin = () => {
    let userRole = localStorage.getItem("userRole");
    return userRole && userRole.toLowerCase() === "syndicator(limited)" ? true : false;
  };
  const handleSearchChange = (value: string) => {
    onFilterChange("searchTerm", value);
  };

  const handleSelectChange = (e: SelectChangeEvent<string>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelectItem = (row: any) => {
    if (selectedSyndicatorRow && selectedSyndicatorRow.id === row.id) {
      setSelectedSyndicatorRow(null);
    } else {
      setSelectedSyndicatorRow(row);
    }
  };

  const handleResetFilters = () => {
    setSelectedSyndicatorIds([]);
    setSelectedSyndicatorNames([]);
    setSelectedSyndicator(null);
    setDateFilters({
      fromDate: null,
      toDate: null,
      byPeriod: null,
    });

    setFilters({
      fromDate: null,
      toDate: null,
      byPeriod: null,
      syndicatorIds: [],
      syndicatorNames: [],
      searchTerm: "",
      label: null,
    });

    setSearchQuery("");
    setSelectedSyndicatorRow(null);
    setRangeOpen(false);
    setSyndicatorsMenuAnchor(null);
    fetchSummaryData();
  };

  const selectAllRows = () => {
    setSelectedSyndicatorRow(null);
  };

  const getRowId = (row: any) => row.id;

  const handleOpenSyndicatorsMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
    setSyndicatorsMenuAnchor(event.currentTarget);
  };

  const handleSelectSyndicator = (syndicator: { id: number; name: string }) => {
    const isSelected = selectedSyndicatorIds.includes(syndicator.id);

    let newIds: number[];
    let newNames: string[];
    if (isSelected) {
      newIds = selectedSyndicatorIds.filter((id) => id !== syndicator.id);
      newNames = selectedSyndicatorNames.filter((name) => name !== syndicator.name);
    } else {
      newIds = [...selectedSyndicatorIds, syndicator.id];
      newNames = [...selectedSyndicatorNames, syndicator.name];
    }

    setSelectedSyndicatorIds(newIds);
    setSelectedSyndicatorNames(newNames);
    onFilterChange("syndicatorIds", newIds);
    onFilterChange("syndicatorNames", newNames);
  };

  const getDisplayText = () => {
    if (selectedSyndicatorNames.length === 0) return "Syndicators";
    if (selectedSyndicatorNames.length === 1) return selectedSyndicatorNames[0];
    return `Syndicators (${selectedSyndicatorNames.length})`;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;

    let newValue = value;

    if (name === "zip") {
      newValue = value.replace(/\D/g, "").slice(0, 5);
    } else if (name === "phoneNumber") {
      const numbers = value.replace(/\D/g, "");
      const char = numbers.split("");
      if (char.length === 0) {
        newValue = "";
      } else if (char.length <= 3) {
        newValue = `(${char.join("")}`;
      } else if (char.length <= 6) {
        newValue = `(${char.slice(0, 3).join("")}) ${char.slice(3, 6).join("")}`;
      } else {
        newValue = `(${char.slice(0, 3).join("")}) ${char.slice(3, 6).join("")}-${char
          .slice(6, 10)
          .join("")}`;
      }
      newValue = newValue.slice(0, 14);
    } else if (name === "abaNumber") {
      newValue = value.replace(/\D/g, "");
    }
    setFormData((prev) => ({ ...prev, [name]: newValue }));
    if (errors[name as keyof ValidationErrors]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleInputBlur = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    validateField(name, value);
  };

  const fetchSummaryData = async (fromDate?: Date | null, toDate?: Date | null) => {
    try {
      setLoading(true);
      const tenantId = localStorage.getItem("tenantId") || "1";
      let url = `/fetchSyndicatorsSummary/${tenantId}`;
      const params = new URLSearchParams();

      let effectiveStartDate = fromDate;
      let effectiveEndDate = toDate;

      if (!fromDate && !toDate) {
        effectiveStartDate = new Date(2000, 0, 1);
        effectiveEndDate = new Date();
        effectiveEndDate.setDate(effectiveEndDate.getDate() + 1);
      } else if (effectiveEndDate) {
        if (isToday(effectiveEndDate)) {
          effectiveEndDate = new Date(effectiveEndDate);
          effectiveEndDate.setDate(effectiveEndDate.getDate() + 1);
        }
      }

      if (effectiveStartDate) {
        params.append("startDate", effectiveStartDate.toISOString().split("T")[0]); // Format: YYYY-MM-DD
      }
      if (effectiveEndDate) {
        params.append("endDate", effectiveEndDate.toISOString().split("T")[0]); // Format: YYYY-MM-DD
      }
      if (params.toString()) {
        url += `?${params.toString()}`;
      }
      const response = await get(url);
      console.log("API Response from master summary:", response);

      if (response && response.data) {
        const mappedSyndicators = response.data.map(
          (summary: SyndicatorSummary, index: number) => ({
            id: index + 1,
            name: summary.syndicatorName || "",
            labels: summary.labels || [],
            company: "",
            email: "",
            status: "Active",
            phoneNumber: "",
            isLegacy: false,
            partyId: summary.partyId,
            cashAvailable: summary.cashAvailable || 0,
            dealBalanceOwed: summary.dealBalanceOwed || 0,
            payback: summary.paybackRtr || 0,
            cashCollected: summary.cashCollected || 0,
            lastDealFunded: summary.lastDealFunded || "N/A",
            dealsFunded: summary.dealsFunded || 0,
            commissionsPaid: summary.commissionsPaid || 0,
            feesPaid: summary.feesPaid || 0,
            portfolioValue: summary.portfolioValue || 0,
            deposits: summary.deposits || 0,
            withdrawals: summary.withdrawals || 0,
            totalDealsFunded: summary.totalDealsFunded || 0,
            totalOpenDeals: summary.totalOpenDeals || 0,
            totalPaidOff: summary.totalPaidOff || 0,
            totalDefaulted: summary.totalDefaulted || 0,
            avgDealSize: summary.avgDealSize || 0,
            avgFactorRate: summary.avgFactorRate || 0,
            cashOnCashMoic: summary.cashOnCashMoic || 0,
            totalInvested: summary.totalInvested || 0,
          })
        );

        const uniqueLabels = [
          ...new Set(
            response.data
              .flatMap((summary: SyndicatorSummary) => (summary.labels as string[]) || [])
              .filter((label: string) => typeof label === "string" && label.trim() !== "")
          ),
        ] as string[];

        const allLabels = uniqueLabels.map((lbl) => ({
          label: lbl,
          value: lbl,
        }));
        setLabelsMenu(allLabels);

        setSyndicators(mappedSyndicators);
        setSummaryData(response.data);
        setTotalCount(response.data.length);
        //updateSummaryBoxes(response.data);
      } else {
        console.error("Invalid response format from master summary:", response);
      }
    } catch (error) {
      console.error("Error fetching master summary data:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSyndicators = async () => {
    try {
      const tenantId = localStorage.getItem("tenantId") || "1";
      const response = await get(`/fetchInvestorsList/${tenantId}`);

      const investorList = response?.investorsList || [];

      if (Array.isArray(investorList)) {
        setInvestors(investorList);
      } else {
        console.error("Invalid response format from fetchSyndicators", response);
        setInvestors([]);
      }
    } catch (error) {
      console.error("Error fetching syndicators:", error);
      setInvestors([]);
    }
  };

  const fetchUsers = async () => {
    try {
      const tenantId = localStorage.getItem("tenantId") || "1";
      const response = await get(`/fetchUsers/${tenantId}`);

      if (response && Array.isArray(response)) {
        setUsers(response);
      } else {
        console.error("Invalid response format from fetchUsers");
      }
    } catch (error) {
      console.error("Error fetching users:", error);
    }
  };

  const handleSaveSyndicator = async () => {
    console.log("Save button clicked");
    setModalError("");
    if (validateForm()) {
      console.log("Form validated successfully");
      try {
        setLoading(true);
        console.log("Loading set to true");
        const syndicatorData = {
          isInvestor: true,
          isFunder: formData.isFunder,
          name: formData.name,
          email: formData.email,
          phoneNumber: formData.phoneNumber,
          address: {
            address: formData.address,
            city: formData.city,
            zipCode: formData.zip,
            state: formData.state,
          },
          contactPerson: formData.contactPerson,
          contactPersonId: parseInt(formData.contactPersonId) || 0,
          abaNumber: formData.abaNumber,
          accountNumber: formData.accountNumber,
          bankName: formData.bankName,
          allowOverCollected: formData.isOverCollection,
          tenantId: localStorage.getItem("tenantId") || "1",
        };

        console.log("Making API call with data:", syndicatorData);
        const createResponse = await post("/facilitator/createOrUpdateSyndicator", syndicatorData);
        console.log("API response received:", createResponse);

        if (createResponse && createResponse.result === true && createResponse.id) {
          console.log("Success! Syndicator ID:", createResponse.id);
          await fetchSummaryData();
          console.log("Data refreshed successfully");
          showSnackbar("Syndicator created successfully", "success");
          setShowAddModal(false);
          resetFormData();
          setModalError("");
          console.log("Modal closed");
        } else if (createResponse && createResponse.result === false) {
          const errorMessage = createResponse.message || "Failed to create/update syndicator";
          console.error("API returned error:", errorMessage);
          setModalError(errorMessage);
        } else {
          console.error("Invalid response format:", createResponse);
          throw new Error("Failed to create/update syndicator: Invalid response format");
        }
      } catch (error) {
        console.error("Error in handleSaveSyndicator:", error);
      } finally {
        console.log("Finally block executing - setting loading to false");
        setLoading(false);
      }
    } else {
      console.log("Form validation failed");
    }
  };

  const handleLabelFilter = (selectedOptions: any) => {
    onFilterChange("label", Array.isArray(selectedOptions) ? selectedOptions : []);
  };

  const handleDateChange = (filterType: string, date: any) => {
    const actualDate = Array.isArray(date) ? date[0] : date;
    onFilterChange(filterType, actualDate);

    if (filterType === "fromDate") {
      setDateFilters((prev) => {
        console.log("Previous dateFilters:", prev);
        const newFilters: DateFilters = {
          ...prev,
          fromDate: actualDate,
          byPeriod: null as string | null,
        };

        onFilterChange("byPeriod", null);
        const fromDate = actualDate ? new Date(actualDate) : null;
        let toDate = prev.toDate ? new Date(prev.toDate) : new Date();

        if (!prev.toDate || isToday(toDate)) {
          toDate.setDate(toDate.getDate() + 1);
        }

        console.log("Calling fetchSummaryData with:", fromDate, toDate);
        if (fromDate) {
          fetchSummaryData(fromDate, toDate);
        }
        return newFilters;
      });
    }

    if (filterType === "toDate") {
      setDateFilters((prev) => {
        console.log("Previous dateFilters:", prev);
        const newFilters: DateFilters = {
          ...prev,
          toDate: actualDate,
          byPeriod: null as string | null,
        };
        onFilterChange("byPeriod", null);

        const fromDate = prev.fromDate ? new Date(prev.fromDate) : new Date(2000, 0, 1);
        let toDate = actualDate ? new Date(actualDate) : null;

        if (toDate) {
          if (isToday(toDate)) {
            toDate.setDate(toDate.getDate() + 1);
          }
          console.log("Calling fetchSummaryData with:", fromDate, toDate);
          fetchSummaryData(fromDate, toDate);
        }
        return newFilters;
      });
    }

    if (filterType === "byPeriod" && actualDate) {
      const now = new Date();
      let startDate = new Date();
      const newFilters: DateFilters = {
        fromDate: null,
        toDate: null,
        byPeriod: actualDate,
      };
      setDateFilters(newFilters);
      onFilterChange("fromDate", null);
      onFilterChange("toDate", null);

      const endDate = new Date(now);
      endDate.setDate(endDate.getDate() + 1);

      switch (actualDate) {
        case "Today":
          startDate.setHours(0, 0, 0, 0);
          console.log("Period: Today - Calling API");
          fetchSummaryData(startDate, endDate);
          break;
        case "This Week":
          startDate = new Date(now);
          startDate.setDate(now.getDate() - 7);
          console.log("Period: This Week - Calling API");
          fetchSummaryData(startDate, endDate);
          break;
        case "This Month":
          startDate = new Date(now);
          startDate.setMonth(now.getMonth() - 1);
          console.log("Period: This Month - Calling API");
          fetchSummaryData(startDate, endDate);
          break;
        case "This Year":
          startDate = new Date(now);
          startDate.setFullYear(now.getFullYear() - 1);
          console.log("Period: This Year - Calling API");
          fetchSummaryData(startDate, endDate);
          break;
        case "All Time":
          startDate = new Date(2000, 0, 1);
          console.log("Period: All Time - Calling API");
          fetchSummaryData(startDate, endDate);
          break;
        default:
          break;
      }
    }
  };

  const handleRowClick = (syndicator: any) => {
    setSelectedSyndicator(syndicator);
  };

  const handleAddSyndicator = () => {
    setShowAddModal(true);
  };

  const handleViewDetails = (partyId: number) => {
    const selectedSyndicator = summaryData.find(
      (item: SyndicatorSummary) => item.partyId === partyId
    );

    if (!selectedSyndicator) {
      console.warn("Selected syndicator not found for partyId:", partyId);
      return;
    }
    setSelectedSyndicator(selectedSyndicator);
    window.location.href = `/#/synd/details/${partyId}`;
  };

  const handleRowDoubleClick = (row: any) => {
    if (row && row.partyId) {
      const selectedSyndicator = summaryData.find(
        (item: SyndicatorSummary) => item.partyId === row.partyId
      );

      if (!selectedSyndicator) {
        console.warn("Selected syndicator not found for partyId:", row.partyId);
        return;
      }

      setSelectedSyndicator(selectedSyndicator);
      window.location.href = `/#/synd/details/${row.partyId}`;
    }
  };

  const handleExportLedgerReport = async () => {
    try {
      setLedgerLoading(true);

      if (selectedSyndicatorRow) {
        const payload = {
          partyId: selectedSyndicatorRow.partyId,
          isSyndicatorLimited: isSyndicatorLimitedLogin(),
        };
        await post("/sendInvestorReport", payload);
      } else {
        await post("/sendMasterLedgerReport");
      }
      setShowLedgerModal(true);
    } catch (error) {
      console.error("Error generating ledger report:", error);
    } finally {
      setLedgerLoading(false);
    }
  };

  const handleInvestorReport = async () => {
    setReportsMenuAnchor(null);

    if (!selectedSyndicatorRow) {
      console.error("No syndicator selected");
      showSnackbar("Please select a syndicator first", "warning");
      return;
    }

    try {
      setLedgerLoading(true);
      const partyId = selectedSyndicatorRow.partyId;
      let startDate: Date | null = null;
      let endDate: Date | null = null;

      if (dateFilters.byPeriod) {
        const now = new Date();
        switch (dateFilters.byPeriod) {
          case "Today":
            startDate = new Date(now);
            startDate.setHours(0, 0, 0, 0);
            endDate = now;
            break;
          case "This Week":
            startDate = new Date(now);
            startDate.setDate(now.getDate() - 7);
            endDate = now;
            break;
          case "This Month":
            startDate = new Date(now);
            startDate.setMonth(now.getMonth() - 1);
            endDate = now;
            break;
          case "This Year":
            startDate = new Date(now);
            startDate.setFullYear(now.getFullYear() - 1);
            endDate = now;
            break;
          case "All Time":
            startDate = new Date(2000, 0, 1);
            endDate = now;
            break;
          default:
            break;
        }
      } else {
        if (dateFilters.fromDate && dateFilters.toDate) {
          startDate = new Date(dateFilters.fromDate);
          endDate = new Date(dateFilters.toDate);
        } else if (dateFilters.fromDate && !dateFilters.toDate) {
          startDate = new Date(dateFilters.fromDate);
          endDate = new Date();
        } else if (!dateFilters.fromDate && dateFilters.toDate) {
          startDate = new Date(2000, 0, 1);
          endDate = new Date(dateFilters.toDate);
        } else {
          startDate = new Date(2000, 0, 1);
          endDate = new Date();
        }
      }

      console.log(
        "Calling API with partyId:",
        partyId,
        "startDate:",
        startDate,
        "endDate:",
        endDate,
        "byPeriod:",
        dateFilters.byPeriod
      );

      let url = `/generateInvestorReport/${partyId}`;
      const params = new URLSearchParams();

      if (startDate) {
        params.append("startDate", startDate.toISOString().split("T")[0]);
      }
      if (endDate) {
        params.append("endDate", endDate.toISOString().split("T")[0]);
      }

      if (params.toString()) {
        url += `?${params.toString()}`;
      }

      console.log("Final URL:", url);

      const response = await get(url);
      console.log("API Response:", response);

      if (response.result) {
        downloadFile(response.s3Url);
      } else {
        showSnackbar(response.message || "Unable to download investor report", "error");
      }
    } catch (error: any) {
      showSnackbar(error.message || "Failed to generate investor report", "error");
    } finally {
      setLedgerLoading(false);
    }
  };

  const shouldShowInvestmentReport = () => {
    const tenant = localStorage.getItem("currentTenant");
    const allowedTenants = ["pangolier", "sandbox", "rowan", "smartmca_sales"];
    return allowedTenants.includes(tenant);
  };

  const downloadFile = (filePath: string) => {
    fetch(filePath, {
      method: "GET",
      headers: new Headers({
        Authorization: "Bearer " + localStorage.getItem(ACCESS_TOKEN),
      }),
    })
      .then((response) => response.blob())
      .then((blob) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = filePath.substr(filePath.lastIndexOf("/") + 1);
        document.body.appendChild(a);
        a.click();
        a.remove();
      });
  };

  const handleCloseLedgerModal = () => {
    setShowLedgerModal(false);
  };

  const validateField = (name: string, value: string) => {
    const rule = validationRules[name];
    let error = "";

    if (rule && rule.required && !value.trim()) {
      error = rule.message;
    } else if (name === "zip" && value && !validateZipOrPostalCode(value, "US")) {
      // Default to US for syndicators, but allow Canadian format as well
      if (!validateZipOrPostalCode(value, "CA")) {
        error = "5-digit ZIP code or Canadian postal code (A1A 1A1) required";
      }
    } else if (rule && rule.pattern && value && !rule.pattern.test(value)) {
      error = rule.message;
    } else if (rule && rule.validate && !rule.validate(value)) {
      error = rule.message;
    }
    setErrors((prev) => ({ ...prev, [name]: error }));
    return !error;
  };

  const validateForm = () => {
    const newErrors: ValidationErrors = {};
    let isValid = true;

    Object.keys(validationRules).forEach((key) => {
      const rule = validationRules[key];
      const value = formData[key as keyof typeof formData] as string;

      if (rule.required && !value.trim()) {
        newErrors[key as keyof ValidationErrors] = rule.message;
        isValid = false;
      } else if (rule.pattern && value && !rule.pattern.test(value)) {
        newErrors[key as keyof ValidationErrors] = rule.message;
        isValid = false;
      } else if (rule.validate && value && !rule.validate(value)) {
        newErrors[key as keyof ValidationErrors] = rule.message;
        isValid = false;
      }
    });
    setErrors(newErrors);
    return isValid;
  };

  const hasFormErrors = () => {
    return Object.values(errors).some((error) => error !== "");
  };

  const handleCloseModal = () => {
    resetFormData();
    setModalError("");
    setShowAddModal(false);
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    setFormData((prev) => ({ ...prev, [name]: checked }));
  };

  const handleExportData = (rows?: any[]) => {
    if (!rows) return;
    const columns = [
      { Header: "Syndicator Name", accessor: "name" },
      { Header: "Cash Available", accessor: "cashAvailable" },
      { Header: "Deal Balance Owed", accessor: "dealBalanceOwed" },
      { Header: "Payback (RTR)", accessor: "payback" },
      { Header: "Cash Collected", accessor: "cashCollected" },
      { Header: "Last Deal Funded", accessor: "lastDealFunded" },
      { Header: "Deals Funded", accessor: "dealsFunded" },
      { Header: "Commissions Paid", accessor: "commissionsPaid" },
      { Header: "Fees Paid", accessor: "feesPaid" },
      { Header: "Portfolio Value", accessor: "portfolioValue" },
      { Header: "Deposits", accessor: "deposits" },
      { Header: "Withdrawals", accessor: "withdrawals" },
    ];

    const processedRows = rows.map((row) => {
      const r = row.original || row;
      return {
        name: r.name || "",
        cashAvailable: r.cashAvailable ?? 0,
        dealBalanceOwed: r.dealBalanceOwed ?? 0,
        payback: r.payback ?? 0,
        cashCollected: r.cashCollected ?? 0,
        lastDealFunded: r.lastDealFunded || "",
        dealsFunded: r.dealsFunded ?? 0,
        commissionsPaid: r.commissionsPaid ?? 0,
        feesPaid: r.feesPaid ?? 0,
        portfolioValue: r.portfolioValue ?? 0,
        deposits: r.deposits ?? 0,
        withdrawals: r.withdrawals ?? 0,
      };
    });

    ExcelExportUtil.exportToExcel(columns, processedRows, "Syndicators", "syndicators_export.xlsx");
  };

  const onFilterChange = (key: string, value: any) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  function matchesFilters(syndicator: any, filters: any) {
    const { syndicatorIds, searchTerm } = filters;
    const matchesSyndicator =
      syndicatorIds.length === 0 || syndicatorIds.includes(syndicator.partyId);
    const matchesSearch =
      !searchTerm ||
      syndicator.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      Object.values(syndicator).join(" ").toLowerCase().includes(searchTerm.toLowerCase());

    const matchesLabel =
      !filters.label ||
      filters.label.length === 0 ||
      (Array.isArray(syndicator.labels) &&
        Array.isArray(filters.label) &&
        filters.label.some((selected: any) => syndicator.labels.includes(selected.label)));

    return matchesSyndicator && matchesSearch && matchesLabel;
  }

  const { filteredSyndicators, filteredSummaryData } = useMemo(() => {
    const filtered = syndicators.filter((syndicator) => matchesFilters(syndicator, filters));
    const filteredTotals = filtered.reduce(
      (acc, item) => ({
        cashAvailable: acc.cashAvailable + (item.cashAvailable || 0),
        dealBalanceOwed: acc.dealBalanceOwed + (item.dealBalanceOwed || 0),
        payback: acc.payback + (item.payback || 0),
        cashCollected: acc.cashCollected + (item.cashCollected || 0),
        dealsFunded: acc.dealsFunded + (item.dealsFunded || 0),
        commissionsPaid: acc.commissionsPaid + (item.commissionsPaid || 0),
        feesPaid: acc.feesPaid + (item.feesPaid || 0),
        portfolioValue: acc.portfolioValue + (item.portfolioValue || 0),
        deposits: acc.deposits + (item.deposits || 0),
        withdrawals: acc.withdrawals + (item.withdrawals || 0),
      }),
      {
        cashAvailable: 0,
        dealBalanceOwed: 0,
        payback: 0,
        cashCollected: 0,
        dealsFunded: 0,
        commissionsPaid: 0,
        feesPaid: 0,
        portfolioValue: 0,
        deposits: 0,
        withdrawals: 0,
      }
    );

    return {
      filteredSyndicators: filtered,
      filteredSummaryData: filteredTotals,
    };
  }, [syndicators, filters]);

  useEffect(() => {
    if (filteredSyndicators.length > 0) {
      const filteredSummaryDataForBoxes = filteredSyndicators.map((syndicator) => ({
        partyId: syndicator.partyId,
        syndicatorName: syndicator.name,
        cashAvailable: syndicator.cashAvailable || 0,
        dealBalanceOwed: syndicator.dealBalanceOwed || 0,
        paybackRtr: syndicator.payback || 0,
        labels: syndicator.labels || [],
        cashCollected: syndicator.cashCollected || 0,
        lastDealFunded: syndicator.lastDealFunded || null,
        dealsFunded: syndicator.dealsFunded || 0,
        commissionsPaid: syndicator.commissionsPaid || 0,
        feesPaid: syndicator.feesPaid || 0,
        portfolioValue: syndicator.portfolioValue || 0,
        deposits: syndicator.deposits || 0,
        withdrawals: syndicator.withdrawals || 0,
        totalDealsFunded: syndicator.totalDealsFunded || syndicator.dealsFunded || 0,
        totalOpenDeals: syndicator.totalOpenDeals || 0,
        totalPaidOff: syndicator.totalPaidOff || 0,
        totalDefaulted: syndicator.totalDefaulted || 0,
        avgDealSize: syndicator.avgDealSize || 0,
        avgFactorRate: syndicator.avgFactorRate || 0,
        cashOnCashMoic: syndicator.cashOnCashMoic || 0,
        totalInvested: syndicator.totalInvested || 0,
      }));

      updateSummaryBoxes(filteredSummaryDataForBoxes);
    } else {
      updateSummaryBoxes([]);
    }
  }, [filteredSyndicators]);

  return (
    <Box
      sx={{
        p: 2,
        height: "100vh",
        overflow: "hidden",
        marginLeft: "250px",
        width: "calc(100% - 250px)",
        transition: "all 0.3s ease",
      }}
    >
      {displayLedgerReportAlert && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Master ledger report has been generated and sent successfully.
        </Alert>
      )}
      <Card elevation={3} sx={{ width: "100%", mb: 1, ml: 1 }}>
        <CardContent sx={{ py: 1.5 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 0,
            }}
          >
            <MDButton
              variant="gradient"
              color="primary"
              sx={{
                height: "40px",
                width: 80,
                fontSize: "11px",
              }}
              onClick={handleAddSyndicator}
            >
              <AddIcon />
              Add
            </MDButton>

            <Tooltip
              title={!selectedSyndicatorRow ? "Please select the syndicator" : ""}
              arrow
              placement="top"
            >
              <span>
                {" "}
                <MDButton
                  color="black"
                  onClick={() =>
                    selectedSyndicatorRow ? handleViewDetails(selectedSyndicatorRow.partyId) : null
                  }
                  disabled={!selectedSyndicatorRow}
                  sx={{
                    height: "40px",
                    width: 50,
                    fontSize: "12px",
                  }}
                >
                  View
                </MDButton>
              </span>
            </Tooltip>

            <Tooltip title="Reset All Filters" arrow>
              <IconButton
                size="medium"
                onClick={handleResetFilters}
                sx={{
                  height: "40px",
                  width: 25,
                  border: "1px solid white",
                  backgroundColor: "white",
                }}
              >
                <Icon sx={{ fontSize: 32 }}>filter_alt_off</Icon>
              </IconButton>
            </Tooltip>

            <Button
              variant="outlined"
              endIcon={<ExpandMore />}
              onClick={handleOpenSyndicatorsMenu}
              size="small"
              sx={{
                height: "40px",
                width: 180,
                fontSize: "14px",
                backgroundColor: "#ffffff",
                color: "#666666",
                border: "1px solid #ccc",
                borderRadius: "10px",
                textTransform: "none",
                fontWeight: 500,
                "&:hover": {
                  borderColor: "#bdbdbd",
                  backgroundColor: "#f5f5f5",
                },
              }}
            >
              {getDisplayText()}
            </Button>
            <Menu
              anchorEl={syndicatorsMenuAnchor}
              open={Boolean(syndicatorsMenuAnchor)}
              onClose={() => setSyndicatorsMenuAnchor(null)}
              PaperProps={{
                sx: {
                  maxHeight: 255,
                  width: 185,
                },
              }}
            >
              {investors.length === 0 ? (
                <MenuItem disabled>No syndicators found</MenuItem>
              ) : (
                investors.map((inv) => {
                  const isSelected = selectedSyndicatorIds.includes(inv.id);
                  return (
                    <Tooltip key={inv.id} title={inv.name} placement="left" arrow>
                      <MenuItem
                        onClick={() => handleSelectSyndicator({ id: inv.id, name: inv.name })}
                        sx={{
                          backgroundColor: isSelected ? "rgba(25, 118, 210, 0.08)" : "transparent",
                          padding: "2px 4px",
                          minHeight: "50px",
                        }}
                      >
                        <Checkbox
                          checked={isSelected}
                          size="small"
                          sx={{
                            padding: "2px",
                            marginRight: "4px",
                            transform: "scale(0.8)",
                            "&.Mui-checked": {
                              color: "primary.main",
                            },
                            "& .MuiSvgIcon-root": {
                              fontSize: "8px",
                            },
                          }}
                        />
                        <Typography
                          variant="body2"
                          sx={{
                            fontSize: "0.9rem",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            flex: 1,
                            lineHeight: 1.7,
                          }}
                        >
                          {inv.name}
                        </Typography>
                      </MenuItem>
                    </Tooltip>
                  );
                })
              )}
            </Menu>

            <Box display="flex" alignItems="center" gap={0.5}>
              <Box sx={{ flex: "0 0 clamp(180px, 25%, 180px)", minWidth: 100, maxWidth: "180px" }}>
                <Autocomplete
                  multiple
                  options={labelsMenu}
                  value={filters.label || []}
                  onChange={(event, newValue) => handleLabelFilter(newValue)}
                  getOptionLabel={(option) => option.label}
                  disableClearable={true}
                  isOptionEqualToValue={(o, v) => o.value === v.value}
                  renderTags={(selected) => {
                    if (!selected || selected.length === 0) return null;

                    const first = selected[0];
                    const numItems = selected.length;

                    if (numItems === 1) {
                      return (
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            backgroundColor: "#F3F4F6",
                            padding: "2px 8px",
                            borderRadius: "6px",
                            fontSize: "12px",
                            marginRight: 6,
                          }}
                        >
                          <div
                            style={{
                              width: 10,
                              height: 10,
                              borderRadius: 3,
                              backgroundColor: getPastelColor(first.label),
                              marginRight: 6,
                            }}
                          ></div>
                          {first.label}
                        </div>
                      );
                    }

                    return (
                      <div
                        style={{
                          backgroundColor: "#E5E7EB",
                          color: "#374151",
                          padding: "2px 8px",
                          borderRadius: "6px",
                          fontSize: "12px",
                        }}
                      >
                        {numItems} Selected
                      </div>
                    );
                  }}
                  renderOption={(props, option) => (
                    <li
                      {...props}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "10px",
                        padding: "6px 10px",
                        fontSize: "13px",
                      }}
                    >
                      <div
                        style={{
                          width: 14,
                          height: 14,
                          borderRadius: 4,
                          backgroundColor: getPastelColor(option.label),
                        }}
                      />
                      {option.label}
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder={filters.label?.length > 0 ? "" : "Filter by Label"}
                      size="small"
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <>
                            <FilterAlt
                              sx={{
                                fontSize: 20,
                                color: "#6B7280",
                                ml: 1,
                                mr: 1,
                              }}
                            />
                            {params.InputProps.startAdornment}
                          </>
                        ),
                      }}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          height: 42,
                          borderRadius: "8px",
                          fontSize: "13px",
                          paddingLeft: "2px",
                          display: "flex",
                          alignItems: "center",
                        },
                      }}
                    />
                  )}
                  sx={{
                    width: "100%",
                    "& .MuiAutocomplete-option": {
                      fontSize: "13px",
                    },
                  }}
                />
              </Box>

              <MDDatePicker
                input={{
                  placeholder: "Filter From Date",
                  sx: {
                    "& .MuiInputBase-root": {
                      height: "40px",
                      fontSize: "0.875rem",
                    },
                  },
                }}
                value={filters.fromDate}
                onChange={(date: any) => handleDateChange("fromDate", date)}
              />

              <Box sx={{ ml: 1 }}>
                <MDDatePicker
                  input={{
                    placeholder: "Filter To Date",
                    sx: {
                      "& .MuiInputBase-root": {
                        height: "40px",
                        fontSize: "0.875rem",
                      },
                    },
                  }}
                  value={filters.toDate}
                  onChange={(date: any) => handleDateChange("toDate", date)}
                />
              </Box>
              <Select
                color="primary"
                value={filters.byPeriod || ""}
                onChange={(e) => handleDateChange("byPeriod", e.target.value)}
                open={rangeOpen}
                onOpen={() => setRangeOpen(true)}
                onClose={() => setRangeOpen(false)}
                displayEmpty
                renderValue={(selected) => (
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 400,
                      fontSize: "0.8rem",
                      color: selected ? "#666666" : "#999999",
                      ml: 1,
                      fontStyle: "normal",
                    }}
                  >
                    {selected || "Filter by Period"}
                  </Typography>
                )}
                sx={{
                  height: "40px",
                  display: "flex",
                  alignItems: "center",
                  minWidth: 120,
                  paddingLeft: 2,
                  borderRadius: "10px",
                  backgroundColor: "#ffffff",
                  fontSize: "0.8rem",
                  "& .MuiOutlinedInput-notchedOutline": {
                    border: "1px solid #d9d9d9",
                    borderRadius: "10px",
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#bdbdbd",
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#bdbdbd",
                    borderWidth: "1px",
                  },
                  "&:hover": {
                    backgroundColor: "#f5f5f5",
                  },
                }}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      "& .MuiMenuItem-root": {
                        fontSize: "0.8rem",
                        "&.Mui-selected": {
                          backgroundColor: "rgba(25, 118, 210, 0.08)",
                          "&:hover": { backgroundColor: "rgba(25, 118, 210, 0.12)" },
                        },
                      },
                    },
                  },
                }}
                IconComponent={() => (
                  <IconButton
                    size="small"
                    onClick={() => setRangeOpen((prev) => !prev)}
                    sx={{ padding: 0, position: "absolute", left: 10, color: "#999999" }}
                  >
                    <Icon fontSize="small">query_builder</Icon>
                  </IconButton>
                )}
                endAdornment={
                  <IconButton
                    size="small"
                    onClick={() => setRangeOpen((prev) => !prev)}
                    sx={{ padding: 0, margin: 0, color: "#666666" }}
                  >
                    <ExpandMore />
                  </IconButton>
                }
              >
                {["All Time", "Today", "This Week", "This Month", "This Year"].map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </Select>
            </Box>

            <TextField
              placeholder="Search..."
              value={filters.searchTerm}
              onChange={(e) => handleSearchChange(e.target.value)}
              size="small"
              sx={{
                height: "40px",
                width: 170,
                "& .MuiInputBase-root": {
                  height: "40px",
                },
                "& .MuiOutlinedInput-input": {
                  padding: "8px 12px",
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />

            <Button
              variant="outlined"
              startIcon={<ExportIcon />}
              size="small"
              onClick={() => handleExportData(syndicators)}
              sx={{
                height: "40px",
                fontSize: "14px",
                backgroundColor: "#ffffff",
                color: "#666666",
                width: 120,
                border: "1px solid #ccc",
                borderRadius: "10px",
                textTransform: "none",
                fontWeight: 500,
                "&:hover": {
                  borderColor: "#bdbdbd",
                  backgroundColor: "#f5f5f5",
                },
              }}
            >
              Export
            </Button>

            <Button
              variant="outlined"
              startIcon={<ReportIcon />}
              endIcon={<ExpandMore />}
              onClick={(e) => setReportsMenuAnchor(e.currentTarget)}
              size="small"
              disabled={ledgerLoading}
              sx={{
                height: "40px",
                fontSize: "14px",
                backgroundColor: "#ffffff",
                color: "#666666",
                minWidth: 120,
                border: "1px solid #ccc",
                borderRadius: "10px",
                textTransform: "none",
                fontWeight: 500,
                "&:hover": {
                  borderColor: "#bdbdbd",
                  backgroundColor: "#f5f5f5",
                },
              }}
            >
              Reports
            </Button>
            <Menu
              anchorEl={reportsMenuAnchor}
              open={Boolean(reportsMenuAnchor)}
              onClose={() => setReportsMenuAnchor(null)}
              PaperProps={{
                sx: {
                  minWidth: 120,
                },
              }}
            >
              <MenuItem onClick={handleExportLedgerReport}>
                <LedgerIcon sx={{ mr: 1, fontSize: 20 }} />
                Ledger
              </MenuItem>
              {shouldShowInvestmentReport() && (
                <MenuItem onClick={handleInvestorReport}>
                  <ReportIcon sx={{ mr: 1, fontSize: 20 }} />
                  Investment Report
                </MenuItem>
              )}
            </Menu>
          </Box>
        </CardContent>
      </Card>
      <Card
        elevation={3}
        sx={{
          width: "100%",
          mb: 2,
          borderRadius: 2,
          border: "1px solid #e0e0e0",
          ml: 1,
          paddingBottom: "20px",
        }}
      >
        <CardContent sx={{ p: 1 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "nowrap",
              overflowX: "auto",
              gap: 1,
              pb: 1,
              ml: 0.5,
              "&::-webkit-scrollbar": {
                height: "6px",
              },
              "&::-webkit-scrollbar-track": {
                background: "#f1f1f1",
                borderRadius: "10px",
              },
              "&::-webkit-scrollbar-thumb": {
                background: "#888",
                borderRadius: "10px",
              },
              "&::-webkit-scrollbar-thumb:hover": {
                background: "#555",
              },
            }}
          >
            {summaryBoxes.map((item, index) => (
              <Box
                key={index}
                sx={{
                  flex: "1 0 auto",
                  minWidth: "110px",
                  height: 100,
                  p: 1,
                  textAlign: "center",
                  border: `1px solid ${item.color}40`,
                  borderRadius: 2,
                  backgroundColor: `${item.color}08`,
                  transition: "all 0.2s",
                  "&:hover": {
                    transform: "translateY(-2px)",
                    boxShadow: 2,
                    backgroundColor: `${item.color}12`,
                  },
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    color: item.color,
                    fontSize: "1.8rem",
                    fontWeight: 700,
                    lineHeight: 1.0,
                    textAlign: "center",
                    mt: 1,
                    mb: 0,
                  }}
                >
                  {item.value}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    fontSize: "0.9rem",
                    color: "text.secondary",
                    lineHeight: 1.0,
                    textAlign: "center",
                    mt: 0,
                  }}
                >
                  {item.title}
                </Typography>
              </Box>
            ))}
          </Box>

          <Box display="flex" alignItems="center" mt={2} ml={1}>
            <Checkbox size="small" onChange={() => handleRowClick} />
            <Typography variant="subtitle1" fontWeight="bold" fontSize={"1.1rem"}>
              Cash Available
            </Typography>
          </Box>
        </CardContent>

        <Box
          sx={{
            flex: "1 1 auto",
            display: "flex",
            flexDirection: "column",
            overflow: "visible",
            paddingBottom: "30px",
          }}
        >
          <InnerTable
            table={{ columns, rows: filteredSyndicators }}
            totalsRow={filteredSummaryData}
            totalColumns={[
              "cashAvailable",
              "dealBalanceOwed",
              "payback",
              "cashCollected",
              "dealsFunded",
              "commissionsPaid",
              "feesPaid",
              "portfolioValue",
              "deposits",
              "withdrawals",
            ]}
            totalsFormatting={{
              integerColumns: ["dealsFunded"],
              currencyColumns: [
                "cashAvailable",
                "dealBalanceOwed",
                "payback",
                "cashCollected",
                "commissionsPaid",
                "feesPaid",
                "portfolioValue",
                "deposits",
                "withdrawals",
              ],
            }}
            showTotals={true}
            entriesPerPage={{ defaultValue: 25, entries: [25, 50, 75, 100] }}
            paginationEnabled={true}
            onExport={handleExportData}
            hasHeaders={true}
            isSorted={true}
            showTotalEntries={true}
            selectedItem={selectedSyndicatorRow ? [selectedSyndicatorRow.id] : []}
            onSelectItem={(id: number) => {
              const row = syndicators.find((s) => s.id === id);
              if (row) handleSelectItem(row);
            }}
            getRowId={getRowId}
            selectAllRows={selectAllRows}
            enableScroll={true}
            scrollHeight="56vh"
            onRowDoubleClick={handleRowDoubleClick}
          />
        </Box>
      </Card>

      <Dialog
        open={showAddModal}
        onClose={handleCloseModal}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            border: "2px solid #555",
            borderRadius: "8px",
            boxShadow: "0 4px 20px rgba(0, 0, 0, 0.3)",
          },
        }}
      >
        <DialogTitle>SYNDICATOR DETAILS</DialogTitle>
        <DialogContent>
          {modalError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setModalError("")}>
              {modalError}
            </Alert>
          )}
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={4}>
              <TextField
                fullWidth
                label="Name*"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                onBlur={handleInputBlur}
                error={!!errors.name}
                helperText={errors.name}
                variant="standard"
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                fullWidth
                label="Email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                onBlur={handleInputBlur}
                error={!!errors.email}
                helperText={errors.email}
                variant="standard"
              />
            </Grid>

            <Grid item xs={4}>
              <TextField
                fullWidth
                label="Phone Number"
                name="phoneNumber"
                value={formData.phoneNumber}
                onChange={handleInputChange}
                onBlur={handleInputBlur}
                error={!!errors.phoneNumber}
                helperText={errors.phoneNumber}
                variant="standard"
              />
            </Grid>
            <Grid item xs={5}>
              <FormControl fullWidth variant="standard" error={!!errors.contactPerson}>
                <InputLabel>Contact Person*</InputLabel>
                <Select
                  name="contactPerson"
                  value={formData.contactPerson}
                  onChange={(e) => {
                    const selectedUser = users.find(
                      (user) => `${user.firstName} ${user.lastName}` === e.target.value
                    );

                    setFormData((prev) => ({
                      ...prev,
                      contactPerson: e.target.value,
                      contactPersonId: selectedUser ? selectedUser.userId.toString() : "",
                    }));
                  }}
                  onBlur={() => validateField("contactPerson", formData.contactPerson)}
                  onOpen={() => setdropdownOpen(true)}
                  onClose={() => setdropdownOpen(false)}
                  endAdornment={dropdownOpen ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                >
                  {users.map((user) => (
                    <MenuItem key={user.userId} value={`${user.firstName} ${user.lastName}`}>
                      {user.firstName} {user.lastName}
                    </MenuItem>
                  ))}
                </Select>
                {errors.contactPerson && <FormHelperText>{errors.contactPerson}</FormHelperText>}
              </FormControl>
            </Grid>

            <Grid item xs={7}>
              <TextField
                fullWidth
                label="Address"
                name="address"
                value={formData.address}
                onChange={handleInputChange}
                onBlur={handleInputBlur}
                error={!!errors.address}
                helperText={errors.address}
                variant="standard"
              />
            </Grid>

            <Grid item xs={4}>
              <TextField
                fullWidth
                label="City"
                name="city"
                value={formData.city}
                onChange={handleInputChange}
                onBlur={handleInputBlur}
                error={!!errors.city}
                helperText={errors.city}
                variant="standard"
              />
            </Grid>
            <Grid item xs={4}>
              <FormControl fullWidth variant="standard" error={!!errors.state}>
                <InputLabel>State*</InputLabel>
                <Select
                  name="state"
                  value={formData.state}
                  onChange={handleSelectChange}
                  onBlur={() => validateField("state", formData.state)}
                  onOpen={() => setdropdownOpen(true)}
                  onClose={() => setdropdownOpen(false)}
                  endAdornment={dropdownOpen ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                  MenuProps={{
                    PaperProps: { style: { maxHeight: 300 } },
                  }}
                >
                  {states.map((state) => (
                    <MenuItem key={state.short} value={state.value}>
                      {state.label}
                    </MenuItem>
                  ))}
                </Select>
                {errors.state && <FormHelperText>{errors.state}</FormHelperText>}
              </FormControl>
            </Grid>

            <Grid item xs={4}>
              <TextField
                fullWidth
                label="Zip Code"
                name="zip"
                value={formData.zip}
                onChange={handleInputChange}
                onBlur={handleInputBlur}
                error={!!errors.zip}
                helperText={errors.zip}
                variant="standard"
                inputProps={{
                  maxLength: 5,
                  pattern: "[0-9]*",
                  inputMode: "numeric",
                }}
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                fullWidth
                label="BANK NAME"
                name="bankName"
                value={formData.bankName}
                onChange={handleInputChange}
                onBlur={handleInputBlur}
                error={!!errors.bankName}
                helperText={errors.bankName}
                variant="standard"
              />
            </Grid>

            <Grid item xs={4}>
              <TextField
                fullWidth
                label="ABA NUMBER"
                name="abaNumber"
                value={formData.abaNumber}
                onChange={handleInputChange}
                onBlur={handleInputBlur}
                error={!!errors.abaNumber}
                helperText={errors.abaNumber}
                variant="standard"
                inputProps={{
                  inputMode: "numeric",
                  pattern: "[0-9]*",
                  maxLength: 9,
                }}
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                fullWidth
                label="ACCOUNT NUMBER"
                name="accountNumber"
                value={formData.accountNumber}
                onChange={handleInputChange}
                onBlur={handleInputBlur}
                error={!!errors.accountNumber}
                helperText={errors.accountNumber}
                variant="standard"
              />
            </Grid>
            <Grid item xs={3}>
              <Box sx={{ display: "flex", alignItems: "center", height: "100%", pt: 1 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      name="isFunder"
                      checked={formData.isFunder}
                      onChange={handleCheckboxChange}
                      sx={{
                        "& .MuiSvgIcon-root": {
                          border: "1px solid #666",
                          borderRadius: "3px",
                        },
                      }}
                    />
                  }
                  label="Is Funder"
                />
              </Box>
            </Grid>

            <Grid item xs={3.5}>
              <Box sx={{ display: "flex", alignItems: "center", height: "100%", pt: 1 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      name="isOverCollection"
                      checked={formData.isOverCollection}
                      onChange={handleCheckboxChange}
                      sx={{
                        "& .MuiSvgIcon-root": {
                          border: "1px solid #666",
                          borderRadius: "3px",
                        },
                      }}
                    />
                  }
                  label="Allow Over-collection"
                />
              </Box>
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions>
          <MDButton variant="contained" color="error" onClick={handleCloseModal} disabled={loading}>
            Cancel
          </MDButton>
          <MDButton
            variant="contained"
            color="primary"
            onClick={handleSaveSyndicator}
            disabled={loading || hasFormErrors()}
          >
            Save
          </MDButton>
        </DialogActions>
      </Dialog>
      <Dialog
        open={showLedgerModal}
        onClose={handleCloseLedgerModal}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "8px",
            padding: "20px 20px 16px 20px",
            textAlign: "center",
          },
        }}
      >
        <DialogContent sx={{ padding: "10px 10px 4px 10px" }}>
          <Typography variant="body1" sx={{ color: "#333", fontSize: "18px" }}>
            {!selectedSyndicatorRow
              ? "Master ledger report generation initiated in the background and will be emailed to you on completion."
              : `Report generation for ${selectedSyndicatorRow.name} initiated in the background and will be emailed to you on completion.`}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: "center", padding: "10px 10px 16px 10px" }}>
          <Button
            onClick={handleCloseLedgerModal}
            variant="outlined"
            sx={{
              border: "1px solid #ccc",
              borderRadius: "4px",
              color: "#1976d2",
              padding: "8px 24px",
              textTransform: "none",
              fontSize: "18px",
              fontWeight: 400,
              "&:hover": {
                borderColor: "#1976d2",
                backgroundColor: "rgba(25, 118, 210, 0.04)",
                color: "#1976d2",
              },
            }}
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        sx={{
          "& .MuiSnackbar-root": {},
        }}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          variant="filled"
          sx={{
            width: "100%",
            "& .MuiAlert-message": {
              fontSize: "0.875rem",
              lineHeight: 1.4,
              padding: "4px 0",
            },
            "& .MuiAlert-icon": {
              fontSize: "20px",
            },
            "& .MuiSvgIcon-root": {
              fontSize: "16px",
            },
            minHeight: "48px",
            padding: "8px 16px",
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default withDommer(Syndicators);
